// API service for NYC Gigs backend
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.token = localStorage.getItem('authToken');
  }

  // Set auth token
  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('authToken', token);
    } else {
      localStorage.removeItem('authToken');
    }
  }

  // Get auth headers
  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }
    
    return headers;
  }

  // Generic request method
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: this.getHeaders(),
      ...options,
    };

    try {
      const response = await fetch(url, config);
      
      // Handle empty responses
      if (response.status === 204) {
        return null;
      }
      
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Authentication endpoints
  async login(username, password) {
    const data = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
    
    if (data.token) {
      this.setToken(data.token);
    }
    
    return data;
  }

  async register(username, email, password) {
    const data = await this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ username, email, password }),
    });
    
    if (data.token) {
      this.setToken(data.token);
    }
    
    return data;
  }

  async logout() {
    try {
      await this.request('/auth/logout', { method: 'POST' });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.setToken(null);
    }
  }

  async changePassword(currentPassword, newPassword) {
    return await this.request('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
  }

  async getProfile() {
    return await this.request('/auth/profile');
  }

  async updateProfile(updates) {
    return await this.request('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  // Job endpoints
  async getJobs(params = {}) {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString());
      }
    });
    
    const endpoint = queryParams.toString() ? `/?${queryParams.toString()}` : '/';
    return await this.request(`/jobs${endpoint}`);
  }

  async getJob(jobId) {
    return await this.request(`/jobs/${jobId}`);
  }

  async createJob(jobData) {
    return await this.request('/jobs/', {
      method: 'POST',
      body: JSON.stringify(jobData),
    });
  }

  async updateJob(jobId, jobData) {
    return await this.request(`/jobs/${jobId}`, {
      method: 'PUT',
      body: JSON.stringify(jobData),
    });
  }

  async deleteJob(jobId) {
    return await this.request(`/jobs/${jobId}`, {
      method: 'DELETE',
    });
  }

  async toggleJobStatus(jobId) {
    return await this.request(`/jobs/${jobId}/toggle`, {
      method: 'POST',
    });
  }

  async searchJobs(params) {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString());
      }
    });
    
    return await this.request(`/jobs/search?${queryParams.toString()}`);
  }

  async getAllJobs() {
    return await this.request('/jobs/admin');
  }

  async getJobStats() {
    return await this.request('/jobs/stats');
  }

  // Newsletter endpoints
  async subscribeToNewsletter(fullName, email, phone) {
    return await this.request('/newsletter/subscribe', {
      method: 'POST',
      body: JSON.stringify({ full_name: fullName, email, phone }),
    });
  }

  async unsubscribeFromNewsletter(email) {
    return await this.request('/newsletter/unsubscribe', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async getSubscribers() {
    return await this.request('/newsletter/subscribers');
  }

  async getActiveSubscribers() {
    return await this.request('/newsletter/subscribers/active');
  }

  async deleteSubscriber(subscriberId) {
    return await this.request(`/newsletter/subscribers/${subscriberId}`, {
      method: 'DELETE',
    });
  }

  async toggleSubscriberStatus(subscriberId) {
    return await this.request(`/newsletter/subscribers/${subscriberId}/toggle`, {
      method: 'POST',
    });
  }

  async getNewsletterStats() {
    return await this.request('/newsletter/stats');
  }

  // User management (admin only)
  async getUsers() {
    return await this.request('/auth/users');
  }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService; 